import React from "react";
import ReactDOM from "react-dom/client";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Fade,
    Grid,
    MenuItem,
    Autocomplete,
    FormControlLabel,
} from "@mui/material";
import { StyledEngineProvider } from "@mui/material/styles";
import ThemeProvider from "../../theme";
import { LocalizationProvider } from "../../i18n/LocalizationProvider";
import Iconify from "../iconify";
import { enqueueSnackbar } from "notistack";
import axios from "axios";
import { useTranslation } from "react-i18next";

const ReservationPromptDialog = ({ onResolve, onReject, options = {} }) => {
    const { t } = useTranslation();
    const isTabsPos = !!options?.isTabsPos;
    const initialData = options?.initialData || {};

    const estimatedPreparationTimeOptions = React.useMemo(() => {
        const list = [];
        for (let t = 15; t <= 90; t += 5) list.push(t);
        return list;
    }, []);

    const reservedDesignatedNumbers = React.useMemo(() => {
        const raw = Array.isArray(options?.reservedDesignatedNumbers)
            ? options.reservedDesignatedNumbers
            : [];
        const nums = raw
            .map((v) => parseInt(v, 10))
            .filter((n) => Number.isFinite(n) && n >= 1 && n <= 200);
        return new Set(nums);
    }, [options?.reservedDesignatedNumbers]);

    // The order's own designated number should always be selectable even if
    // it appears in the reserved set (it's reserved by *this* order).
    const ownDesignatedNumber = React.useMemo(() => {
        const raw = initialData.designated_number;
        if (raw === null || raw === undefined || raw === "") return null;
        const n = parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
    }, [initialData.designated_number]);

    const designatedNumberOptions = React.useMemo(() => {
        const list = [];
        for (let n = 1; n <= 200; n += 1) {
            if (!reservedDesignatedNumbers.has(n) || n === ownDesignatedNumber) {
                list.push(n);
            }
        }
        return list;
    }, [reservedDesignatedNumbers, ownDesignatedNumber]);

    const [formData, setFormData] = React.useState(() => {
        if (isTabsPos) {
            // designated_number must be a string to match <MenuItem value={String(n)}>
            const rawDN = initialData.designated_number;
            const designatedStr =
                rawDN === null || rawDN === undefined || rawDN === ""
                    ? ""
                    : String(rawDN);
            return {
                notes: initialData.notes || "",
                estimated_preparation_time: initialData.estimated_preparation_time || 15,
                designated_number: designatedStr,
                use_delivery_provider: initialData.use_delivery_provider || false,
                delivery_provider: initialData.delivery_provider || "",
                customer: initialData.customer || "",
                mobile_no: initialData.mobile_no || "",
                address_line1: initialData.address_line1 || "",
                address_line2: initialData.address_line2 || "",
            };
        }
        return {
            notes: initialData.notes || "",
            adults: initialData.adults || "",
            kids: initialData.kids || "",
            babies: initialData.babies || "",
        };
    });
    const [open, setOpen] = React.useState(true);

    const {
        title = "Reservation Details",
        message = "Please enter the reservation details:",
        confirmText = "Confirm",
        cancelText = "Cancel",
        showSaveDraftButton = false,
        saveDraftText = "Save",
    } = options;

    const dialogTitle = React.useMemo(() => {
        if (!isTabsPos) return title;
        const hasDesignated = !!formData?.designated_number;
        return `${title} (${hasDesignated ? "dine-in" : "take-away"})`;
    }, [isTabsPos, title, formData?.designated_number]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleNumberChange = (field, value) => {
        // Allow empty string, otherwise ensure it's a valid positive number
        if (value === "" || value === null || value === undefined) {
            handleInputChange(field, "");
        } else {
            const numValue = Math.max(0, parseInt(value) || 0);
            handleInputChange(field, numValue);
        }
    };

    const [deliveryProviders, setDeliveryProviders] = React.useState([]);
    const [deliveryProvidersLoading, setDeliveryProvidersLoading] = React.useState(false);
    const [deliveryProviderInputValue, setDeliveryProviderInputValue] = React.useState(
        initialData.delivery_provider || ""
    );

    const fetchDeliveryProviders = React.useCallback(async () => {
        setDeliveryProvidersLoading(true);
        try {
            const params = {
                fields: JSON.stringify(["name", "company"]),
                limit_page_length: 0,
            };
            const { data } = await axios.get("resource/Delivery Providers", { params });
            const list = Array.isArray(data?.data) ? data.data : [];
            setDeliveryProviders(list);
            return list;
        } finally {
            setDeliveryProvidersLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (!isTabsPos) return;
        if (!formData.use_delivery_provider) return;
        fetchDeliveryProviders().then((list) => {
            // If we have a delivery provider from initialData, just ensure the input shows it
            if (formData.delivery_provider && Array.isArray(list)) {
                const match = list.find((p) => p?.name === formData.delivery_provider);
                const currentInput = String(deliveryProviderInputValue || "").trim();
                const shouldHydrateLabel =
                    !currentInput || currentInput === String(formData.delivery_provider);
                if (match && shouldHydrateLabel) {
                    setDeliveryProviderInputValue(match.company || match.name);
                }
            } else if (!formData.delivery_provider && Array.isArray(list) && list.length > 0) {
                setFormData((prev) => ({
                    ...prev,
                    delivery_provider: list[0].name,
                }));
                setDeliveryProviderInputValue(list[0].company || list[0].name);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTabsPos, formData.use_delivery_provider]);

    const [customerOptions, setCustomerOptions] = React.useState([]);
    const [customerLoading, setCustomerLoading] = React.useState(false);
    const [customerInputValue, setCustomerInputValue] = React.useState(
        initialData.customer || ""
    );
    const [mobileOptions, setMobileOptions] = React.useState([]);
    const [mobileLoading, setMobileLoading] = React.useState(false);
    const [mobileInputValue, setMobileInputValue] = React.useState(
        initialData.mobile_no || ""
    );
    const isSyncingRef = React.useRef(false);
    const customerSearchRequestIdRef = React.useRef(0);
    const mobileSearchRequestIdRef = React.useRef(0);
    const hydratedCustomerRef = React.useRef(null);
    const hydratedMobileRef = React.useRef(null);
    const userEditedDeliveryFieldsRef = React.useRef({
        mobile_no: false,
        address_line1: false,
        address_line2: false,
    });
    const initialDeliveryCustomerRef = React.useRef(
        String(initialData.customer || "").trim()
    );
    const latestFormDataRef = React.useRef(formData);

    React.useEffect(() => {
        latestFormDataRef.current = formData;
    }, [formData]);

    const resetDeliveryDirtyFields = React.useCallback(() => {
        userEditedDeliveryFieldsRef.current = {
            mobile_no: false,
            address_line1: false,
            address_line2: false,
        };
    }, []);

    const markDeliveryFieldDirty = React.useCallback((field) => {
        if (Object.prototype.hasOwnProperty.call(userEditedDeliveryFieldsRef.current, field)) {
            userEditedDeliveryFieldsRef.current[field] = true;
        }
    }, []);

    const normalizeHtmlText = React.useCallback((html) => {
        if (!html) return "";
        try {
            return String(html).replace(/<[^>]+>/g, "").trim();
        } catch {
            return String(html);
        }
    }, []);

    const parsePrimaryAddressLines = React.useCallback((primaryAddress) => {
        const raw = String(primaryAddress || "");
        if (!raw) return { line1: "", line2: "" };
        const parts = raw.split("<br>").map((p) => normalizeHtmlText(p)).filter(Boolean);
        return { line1: parts[0] || "", line2: parts[1] || "" };
    }, [normalizeHtmlText]);

    const fetchAddressByName = React.useCallback(async (addressName) => {
        const name = String(addressName || "").trim();
        if (!name) return { line1: "", line2: "" };
        try {
            const { data } = await axios.get(
                "resource/Address/" + encodeURIComponent(name)
            );
            const doc = data?.data || {};
            return {
                line1: doc.address_line1 || "",
                line2: doc.address_line2 || "",
            };
        } catch {
            return { line1: "", line2: "" };
        }
    }, []);

    const fetchLatestAddressByCustomer = React.useCallback(async (customerName) => {
        const customer = String(customerName || "").trim();
        if (!customer) return { line1: "", line2: "" };
        try {
            const params = {
                fields: JSON.stringify(["parent"]),
                filters: JSON.stringify([
                    ["Dynamic Link", "link_doctype", "=", "Customer"],
                    ["Dynamic Link", "link_name", "=", customer],
                    ["Dynamic Link", "parenttype", "=", "Address"],
                ]),
                order_by: "modified desc",
                limit_page_length: 1,
            };
            const { data } = await axios.get("resource/Dynamic Link", { params });
            const linkRow = Array.isArray(data?.data) ? data.data[0] : null;
            if (linkRow?.parent) {
                return await fetchAddressByName(linkRow.parent);
            }
        } catch {
            // Fallback below
        }

        // Fallback for setups where Dynamic Link is restricted.
        try {
            const params = {
                fields: JSON.stringify(["name", "address_line1", "address_line2"]),
                filters: JSON.stringify([["Address", "address_title", "=", customer]]),
                order_by: "modified desc",
                limit_page_length: 1,
            };
            const { data } = await axios.get("resource/Address", { params });
            const row = Array.isArray(data?.data) ? data.data[0] : null;
            return {
                line1: row?.address_line1 || "",
                line2: row?.address_line2 || "",
            };
        } catch {
            return { line1: "", line2: "" };
        }
    }, [fetchAddressByName]);

    const resolveCustomerDetails = React.useCallback(
        async (customerDoc = {}) => {
            const nextMobile = String(customerDoc?.mobile_no || "").trim();
            let nextLine1 = "";
            let nextLine2 = "";

            const primaryAddress = customerDoc?.primary_address || "";
            if (!primaryAddress) {
                const linkedAddress = await fetchLatestAddressByCustomer(
                    customerDoc?.name || customerDoc?.customer_name
                );
                return {
                    mobile_no: nextMobile,
                    address_line1: linkedAddress.line1 || "",
                    address_line2: linkedAddress.line2 || "",
                };
            }

            const parsedInline = parsePrimaryAddressLines(primaryAddress);
            if (parsedInline.line1 || parsedInline.line2) {
                nextLine1 = parsedInline.line1 || "";
                nextLine2 = parsedInline.line2 || "";
            } else {
                const resolved = await fetchAddressByName(primaryAddress);
                nextLine1 = resolved.line1 || "";
                nextLine2 = resolved.line2 || "";
            }

            return {
                mobile_no: nextMobile,
                address_line1: nextLine1,
                address_line2: nextLine2,
            };
        },
        [fetchAddressByName, fetchLatestAddressByCustomer, parsePrimaryAddressLines]
    );

    const fetchCustomerMobileNumbers = React.useCallback(async ({ customer, mobile } = {}) => {
        const customerValue = String(customer || "").trim();
        const mobileValue = String(mobile || "").trim();
        if (!customerValue && !mobileValue) return [];

        try {
            const { data } = await axios.get(
                "api/method/restaurant_management.api.get_customer_mobile_numbers",
                {
                    params: {
                        customer: customerValue || undefined,
                        mobile: mobileValue || undefined,
                        limit: 20,
                    },
                }
            );
            return Array.isArray(data?.message) ? data.message : [];
        } catch {
            return [];
        }
    }, []);

    const applySelectedCustomer = React.useCallback(
        async (customerDoc = {}, { force = true } = {}) => {
            const customerName = String(customerDoc?.name || "").trim();
            if (!customerName) return null;

            const customerLabel = String(
                customerDoc?.customer_name || customerName
            ).trim();
            const hydrated = await resolveCustomerDetails(customerDoc);

            isSyncingRef.current = true;
            try {
                setCustomerInputValue(customerLabel || customerName);
                const currentData = latestFormDataRef.current || {};
                const shouldSetMobile =
                    force ||
                    (!userEditedDeliveryFieldsRef.current.mobile_no &&
                        !String(currentData.mobile_no || "").trim());
                const shouldSetLine1 =
                    force ||
                    (!userEditedDeliveryFieldsRef.current.address_line1 &&
                        !String(currentData.address_line1 || "").trim());
                const shouldSetLine2 =
                    force ||
                    (!userEditedDeliveryFieldsRef.current.address_line2 &&
                        !String(currentData.address_line2 || "").trim());
                const nextMobile = shouldSetMobile
                    ? hydrated.mobile_no || ""
                    : currentData.mobile_no;

                if (shouldSetMobile) {
                    setMobileInputValue(nextMobile);
                }

                setFormData((prev) => {
                    return {
                        ...prev,
                        customer: customerName,
                        mobile_no: shouldSetMobile ? nextMobile : prev.mobile_no,
                        address_line1: shouldSetLine1
                            ? hydrated.address_line1 || ""
                            : prev.address_line1,
                        address_line2: shouldSetLine2
                            ? hydrated.address_line2 || ""
                            : prev.address_line2,
                    };
                });
                hydratedCustomerRef.current = customerName;
                if (hydrated.mobile_no) {
                    hydratedMobileRef.current = String(hydrated.mobile_no).trim();
                }
                const customerMobileOptions = await fetchCustomerMobileNumbers({
                    customer: customerName,
                });
                if (customerMobileOptions.length) {
                    setMobileOptions(customerMobileOptions);
                }
            } finally {
                isSyncingRef.current = false;
            }

            return {
                customer: customerName,
                customer_name: customerLabel,
                ...hydrated,
            };
        },
        [fetchCustomerMobileNumbers, resolveCustomerDetails]
    );

    const searchCustomers = React.useCallback(async (q) => {
        const query = String(q || "").trim();
        if (!query) {
            setCustomerOptions([]);
            return;
        }
        const requestId = customerSearchRequestIdRef.current + 1;
        customerSearchRequestIdRef.current = requestId;
        setCustomerLoading(true);
        try {
            const params = {
                fields: JSON.stringify([
                    "name",
                    "customer_name",
                    "mobile_no",
                    "primary_address",
                    "modified",
                ]),
                filters: JSON.stringify([["Customer", "customer_name", "like", `%${query}%`]]),
                order_by: "modified desc",
                limit_page_length: 20,
            };
            const { data } = await axios.get("resource/Customer", { params });
            const list = Array.isArray(data?.data) ? data.data : [];
            if (requestId === customerSearchRequestIdRef.current) {
                setCustomerOptions(list);
            }
        } finally {
            if (requestId === customerSearchRequestIdRef.current) {
                setCustomerLoading(false);
            }
        }
    }, []);

    const searchCustomersByMobile = React.useCallback(async (q) => {
        const query = String(q || "").trim();
        const requestId = mobileSearchRequestIdRef.current + 1;
        mobileSearchRequestIdRef.current = requestId;

        if (!query) {
            const selectedCustomer = String(
                latestFormDataRef.current?.customer || ""
            ).trim();
            if (!selectedCustomer) {
                setMobileOptions([]);
                return;
            }
            setMobileLoading(true);
            try {
                const list = await fetchCustomerMobileNumbers({
                    customer: selectedCustomer,
                });
                if (requestId === mobileSearchRequestIdRef.current) {
                    setMobileOptions(list);
                }
            } finally {
                if (requestId === mobileSearchRequestIdRef.current) {
                    setMobileLoading(false);
                }
            }
            return;
        }

        setMobileLoading(true);
        try {
            const list = await fetchCustomerMobileNumbers({ mobile: query });
            if (requestId === mobileSearchRequestIdRef.current) {
                setMobileOptions(list);
            }
        } finally {
            if (requestId === mobileSearchRequestIdRef.current) {
                setMobileLoading(false);
            }
        }
    }, [fetchCustomerMobileNumbers]);

    const resolveCustomerByValue = React.useCallback(
        async (rawCustomerValue) => {
            const customerValue = String(rawCustomerValue || "").trim();
            if (!customerValue) return null;

            const matched = customerOptions.find(
                (opt) =>
                    opt?.name === customerValue ||
                    String(opt?.customer_name || "").trim() === customerValue
            );
            if (matched) return matched;

            try {
                const paramsByName = {
                    fields: JSON.stringify([
                        "name",
                        "customer_name",
                        "mobile_no",
                        "primary_address",
                    ]),
                    filters: JSON.stringify([["Customer", "name", "=", customerValue]]),
                    limit_page_length: 1,
                };
                const byNameRes = await axios.get("resource/Customer", {
                    params: paramsByName,
                });
                const byName = Array.isArray(byNameRes?.data?.data)
                    ? byNameRes.data.data[0]
                    : null;
                if (byName) return byName;
            } catch {
                // Fallback below.
            }

            try {
                const paramsByCustomerName = {
                    fields: JSON.stringify([
                        "name",
                        "customer_name",
                        "mobile_no",
                        "primary_address",
                    ]),
                    filters: JSON.stringify([
                        ["Customer", "customer_name", "=", customerValue],
                    ]),
                    order_by: "modified desc",
                    limit_page_length: 1,
                };
                const byCustomerNameRes = await axios.get("resource/Customer", {
                    params: paramsByCustomerName,
                });
                return Array.isArray(byCustomerNameRes?.data?.data)
                    ? byCustomerNameRes.data.data[0] || null
                    : null;
            } catch {
                return null;
            }
        },
        [customerOptions]
    );

    const resolveCustomerByMobile = React.useCallback(
        async (rawMobileValue) => {
            const mobileValue = String(rawMobileValue || "").trim();
            if (!mobileValue) return null;

            const exactOption = mobileOptions.find(
                (opt) => String(opt?.mobile_no || "").trim() === mobileValue
            );
            if (exactOption) return exactOption;

            try {
                const params = {
                    fields: JSON.stringify([
                        "name",
                        "customer_name",
                        "mobile_no",
                        "primary_address",
                    ]),
                    filters: JSON.stringify([
                        ["Customer", "mobile_no", "like", `%${mobileValue}%`],
                    ]),
                    order_by: "modified desc",
                    limit_page_length: 1,
                };
                const { data } = await axios.get("resource/Customer", { params });
                return Array.isArray(data?.data) ? data.data[0] || null : null;
            } catch {
                return null;
            }
        },
        [mobileOptions]
    );

    React.useEffect(() => {
        if (!isTabsPos) return;
        if (!formData.use_delivery_provider) return;
        const t = setTimeout(() => searchCustomers(customerInputValue), 300);
        return () => clearTimeout(t);
    }, [isTabsPos, formData.use_delivery_provider, customerInputValue, searchCustomers]);

    React.useEffect(() => {
        if (!isTabsPos) return;
        if (!formData.use_delivery_provider) return;
        const t = setTimeout(() => searchCustomersByMobile(mobileInputValue), 300);
        return () => clearTimeout(t);
    }, [
        isTabsPos,
        formData.use_delivery_provider,
        mobileInputValue,
        searchCustomersByMobile,
    ]);

    // Load customer options on mount if we have a pre-filled customer
    React.useEffect(() => {
        if (!isTabsPos) return;
        if (!formData.use_delivery_provider) return;
        if (initialData.customer && customerInputValue) {
            searchCustomers(customerInputValue);
        }
        if (initialData.mobile_no && mobileInputValue) {
            searchCustomersByMobile(mobileInputValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Hydrate delivery fields when reopening/editing an order that only has customer set.
    React.useEffect(() => {
        if (!isTabsPos || !formData.use_delivery_provider) return;
        const customer = String(formData.customer || "").trim();
        if (!customer) return;
        if (customer !== initialDeliveryCustomerRef.current) return;
        if (hydratedCustomerRef.current === customer) return;
        const currentFormData = latestFormDataRef.current || {};
        if (currentFormData.mobile_no && currentFormData.address_line1) {
            hydratedCustomerRef.current = customer;
            return;
        }
        let isMounted = true;
        const hydrateFromCustomer = async () => {
            const customerDoc = await resolveCustomerByValue(customer);
            if (!isMounted || !customerDoc) return;
            await applySelectedCustomer(customerDoc, { force: false });
        };
        hydrateFromCustomer();
        return () => {
            isMounted = false;
        };
    }, [
        isTabsPos,
        formData.use_delivery_provider,
        formData.customer,
        resolveCustomerByValue,
        applySelectedCustomer,
    ]);

    const resolveWithAction = async (actionType = "confirm") => {
        if (!isTabsPos) {
            if (formData.adults === "" || formData.adults <= 0) {
                enqueueSnackbar("Please enter the number of adults", {
                    variant: "error",
                });
                return;
            }
            const finalData = {
                ...formData,
                adults: formData.adults === "" ? 1 : formData.adults,
                kids: formData.kids === "" ? 0 : formData.kids,
                babies: formData.babies === "" ? 0 : formData.babies,
                __action: actionType,
            };
            setOpen(false);
            onResolve(finalData);
            return;
        }

        if (isTabsPos) {
            let resolvedFormData = { ...formData };

            if (
                resolvedFormData.use_delivery_provider &&
                String(resolvedFormData.customer || "").trim() &&
                (!String(resolvedFormData.mobile_no || "").trim() ||
                    !String(resolvedFormData.address_line1 || "").trim())
            ) {
                const customerValue = String(resolvedFormData.customer || "").trim();
                const selectedCustomerDoc = await resolveCustomerByValue(customerValue);

                if (selectedCustomerDoc) {
                    const hydrated = await resolveCustomerDetails(selectedCustomerDoc);
                    resolvedFormData = {
                        ...resolvedFormData,
                        customer: selectedCustomerDoc.name || resolvedFormData.customer || "",
                        mobile_no:
                            resolvedFormData.mobile_no || hydrated?.mobile_no || "",
                        address_line1:
                            resolvedFormData.address_line1 ||
                            hydrated?.address_line1 ||
                            "",
                        address_line2:
                            resolvedFormData.address_line2 ||
                            hydrated?.address_line2 ||
                            "",
                    };
                }
            }

            if (
                resolvedFormData.use_delivery_provider &&
                !String(resolvedFormData.customer || "").trim() &&
                String(resolvedFormData.mobile_no || "").trim()
            ) {
                const selectedByMobile = await resolveCustomerByMobile(
                    resolvedFormData.mobile_no
                );
                if (selectedByMobile) {
                    const hydrated = await resolveCustomerDetails(selectedByMobile);
                    resolvedFormData = {
                        ...resolvedFormData,
                        customer: selectedByMobile.name || "",
                        mobile_no:
                            resolvedFormData.mobile_no || hydrated?.mobile_no || "",
                        address_line1:
                            resolvedFormData.address_line1 ||
                            hydrated?.address_line1 ||
                            "",
                        address_line2:
                            resolvedFormData.address_line2 ||
                            hydrated?.address_line2 ||
                            "",
                    };
                }
            }

            if (resolvedFormData.use_delivery_provider) {
                if (!resolvedFormData.delivery_provider) {
                    enqueueSnackbar(
                        t("pos.reservationPrompt.validationSelectDeliveryProvider", {
                            defaultValue: "Please select a delivery provider",
                        }),
                        {
                            variant: "error",
                        }
                    );
                    return;
                }
                if (!String(resolvedFormData.customer || "").trim()) {
                    enqueueSnackbar(
                        t("pos.reservationPrompt.validationEnterCustomer", {
                            defaultValue: "Please enter the customer",
                        }),
                        {
                            variant: "error",
                        }
                    );
                    return;
                }
                if (!String(resolvedFormData.mobile_no || "").trim()) {
                    enqueueSnackbar(
                        t("pos.reservationPrompt.validationEnterMobile", {
                            defaultValue: "Please enter the mobile number",
                        }),
                        {
                            variant: "error",
                        }
                    );
                    return;
                }
                if (!String(resolvedFormData.address_line1 || "").trim()) {
                    enqueueSnackbar(
                        t("pos.reservationPrompt.validationEnterAddress", {
                            defaultValue: "Please enter the address",
                        }),
                        {
                            variant: "error",
                        }
                    );
                    return;
                }
            }

            const prepTime = parseInt(
                resolvedFormData.estimated_preparation_time,
                10
            );
            const normalizedPrepTime = Number.isFinite(prepTime)
                ? prepTime
                : 15;

            const designated =
                resolvedFormData.designated_number === "" ||
                resolvedFormData.designated_number === null ||
                resolvedFormData.designated_number === undefined
                    ? ""
                    : String(resolvedFormData.designated_number);

            const finalData = {
                notes: resolvedFormData.notes || "",
                estimated_preparation_time: normalizedPrepTime,
                designated_number: designated,
                use_delivery_provider: !!resolvedFormData.use_delivery_provider,
                delivery_provider: resolvedFormData.use_delivery_provider
                    ? resolvedFormData.delivery_provider || ""
                    : "",
                customer: resolvedFormData.customer || "",
                mobile_no: resolvedFormData.mobile_no || "",
                address_line1: resolvedFormData.address_line1 || "",
                address_line2: resolvedFormData.address_line2 || "",
                __action: actionType,
            };
            setOpen(false);
            onResolve(finalData);
            return;
        }
    };

    const handleConfirm = async () => {
        await resolveWithAction("confirm");
    };
    const handleSaveDraft = async () => {
        await resolveWithAction("save_draft");
    };

    const handleCancel = () => {
        setOpen(false);
        onResolve(null);
    };

    const handleKeyDown = (event) => {
        // Allow Ctrl+Enter or Cmd+Enter to submit
        if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            handleConfirm();
        }
    };

    return (
        <StyledEngineProvider injectFirst>
            <LocalizationProvider>
                <ThemeProvider>
                    <Dialog
                        open={open}
                        onClose={handleCancel}
                        maxWidth="sm"
                        fullWidth
                        TransitionComponent={Fade}
                        transitionDuration={{ enter: 400, exit: 300 }}
                        PaperProps={{
                            sx: (theme) => ({
                                boxShadow:
                                    theme.customShadows?.dialog ||
                                    theme.shadows[24],
                                overflow: "visible",
                            }),
                        }}
                        sx={{
                            "& .MuiBackdrop-root": {
                                backgroundColor: "rgba(0, 0, 0, 0.6)",
                                backdropFilter: "blur(4px)",
                            },
                        }}
                    >
                        <DialogTitle
                            sx={(theme) => ({
                                pb: 1,
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            })}
                        >
                            <Box
                                sx={(theme) => ({
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                    height: 24,
                                    backgroundColor: theme.palette.primary.lighter,
                                    color: theme.palette.primary.main,
                                })}
                            >
                                <Iconify icon="eva:calendar-outline" width={14} />
                            </Box>
                            {dialogTitle}
                        </DialogTitle>
                        <DialogContent sx={{ pb: 1 }}>
                            <DialogContentText
                                sx={(theme) => ({
                                    mb: 2.5,
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.95rem",
                                    lineHeight: 1.5,
                                })}
                            >
                                {message}
                            </DialogContentText>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2.5,
                            }}
                        >
                            {!isTabsPos ? (
                                <>
                                    {/* Guest Count Fields */}
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Adults"
                                                type="number"
                                                fullWidth
                                                variant="outlined"
                                                value={formData.adults}
                                                onChange={(e) =>
                                                    handleNumberChange(
                                                        "adults",
                                                        e.target.value
                                                    )
                                                }
                                                onFocus={(e) => e.target.select()}
                                                onClick={(e) => e.target.select()}
                                                inputProps={{
                                                    min: 1,
                                                    max: 20,
                                                }}
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Kids"
                                                type="number"
                                                fullWidth
                                                variant="outlined"
                                                value={formData.kids}
                                                onChange={(e) =>
                                                    handleNumberChange(
                                                        "kids",
                                                        e.target.value
                                                    )
                                                }
                                                onFocus={(e) => e.target.select()}
                                                onClick={(e) => e.target.select()}
                                                inputProps={{
                                                    min: 0,
                                                    max: 20,
                                                }}
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Babies"
                                                type="number"
                                                fullWidth
                                                variant="outlined"
                                                value={formData.babies}
                                                onChange={(e) =>
                                                    handleNumberChange(
                                                        "babies",
                                                        e.target.value
                                                    )
                                                }
                                                onFocus={(e) => e.target.select()}
                                                onClick={(e) => e.target.select()}
                                                inputProps={{
                                                    min: 0,
                                                    max: 20,
                                                }}
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={formData.use_delivery_provider ? 12 : 6}>
                                            <TextField
                                                autoFocus
                                                select
                                                label="Estimated Preparation Time"
                                                fullWidth
                                                variant="outlined"
                                                value={
                                                    formData.estimated_preparation_time
                                                }
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        "estimated_preparation_time",
                                                        e.target.value
                                                    )
                                                }
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            >
                                                {estimatedPreparationTimeOptions.map(
                                                    (t) => (
                                                        <MenuItem
                                                            key={`prep_${t}`}
                                                            value={t}
                                                        >
                                                            {t}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </TextField>
                                        </Grid>
                                        {!formData.use_delivery_provider ? (
                                            <Grid item xs={6}>
                                                <TextField
                                                    select
                                                    label="Designated Number"
                                                    fullWidth
                                                    variant="outlined"
                                                    value={formData.designated_number}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            "designated_number",
                                                            e.target.value
                                                        )
                                                    }
                                                    sx={(theme) => ({
                                                        "& .MuiOutlinedInput-root": {
                                                            "&:hover .MuiOutlinedInput-notchedOutline":
                                                                {
                                                                    borderColor:
                                                                        theme.palette
                                                                            .primary.main,
                                                                },
                                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                                {
                                                                    borderColor:
                                                                        theme.palette
                                                                            .primary.main,
                                                                    borderWidth: 2,
                                                                },
                                                        },
                                                    })}
                                                >
                                                    <MenuItem value="">
                                                        None
                                                    </MenuItem>
                                                    <MenuItem value="DINE_IN">
                                                        Dine in
                                                    </MenuItem>
                                                    {designatedNumberOptions.map(
                                                        (n) => (
                                                            <MenuItem
                                                                key={`dn_${n}`}
                                                                value={String(n)}
                                                            >
                                                                {n}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </TextField>
                                            </Grid>
                                        ) : null}
                                        <Grid item xs={12} sx={{ pt: "4px !important" }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={!!formData.use_delivery_provider}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            const existingCustomerText =
                                                                String(formData.customer || "").trim();
                                                            const existingMobileText =
                                                                String(formData.mobile_no || "").trim();
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                use_delivery_provider: checked,
                                                                delivery_provider: checked
                                                                    ? prev.delivery_provider
                                                                    : "",
                                                                // Designated number is only for non-delivery flows
                                                                designated_number: checked
                                                                    ? ""
                                                                    : prev.designated_number,
                                                            }));
                                                            if (checked) {
                                                                // Carry over existing plain-text customer into the typeahead input
                                                                if (existingCustomerText) {
                                                                    setCustomerInputValue(existingCustomerText);
                                                                }
                                                                if (existingMobileText) {
                                                                    setMobileInputValue(existingMobileText);
                                                                }
                                                                // If we already have a provider value (typed/selected), reflect it in the input UI
                                                                const providerText = String(
                                                                    formData.delivery_provider || ""
                                                                ).trim();
                                                                if (providerText) {
                                                                    const match = deliveryProviders.find(
                                                                        (p) => p?.name === providerText
                                                                    );
                                                                    setDeliveryProviderInputValue(
                                                                        match?.company || providerText
                                                                    );
                                                                }
                                                            }
                                                        }}
                                                    />
                                                }
                                                label={t(
                                                    "pos.reservationPrompt.useDeliveryProvider",
                                                    { defaultValue: "Use delivery provider" }
                                                )}
                                            />
                                        </Grid>

                                        {formData.use_delivery_provider ? (
                                            <>
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        freeSolo
                                                        loading={deliveryProvidersLoading}
                                                        options={deliveryProviders}
                                                        getOptionLabel={(opt) => {
                                                            if (typeof opt === "string") return opt;
                                                            return opt?.company || opt?.name || "";
                                                        }}
                                                        inputValue={deliveryProviderInputValue}
                                                        onInputChange={(_, value) => {
                                                            const v = value || "";
                                                            setDeliveryProviderInputValue(v);
                                                            // Store typed value; backend will resolve by company (or create) and return docname
                                                            handleInputChange("delivery_provider", v);
                                                        }}
                                                        onChange={(_, newValue) => {
                                                            if (!newValue) return;
                                                            if (typeof newValue === "string") {
                                                                handleInputChange("delivery_provider", newValue);
                                                                return;
                                                            }
                                                            // Store docname for link field, but show company as label
                                                            handleInputChange("delivery_provider", newValue.name);
                                                            setDeliveryProviderInputValue(
                                                                newValue.company || newValue.name
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "pos.reservationPrompt.deliveryProvider",
                                                                    {
                                                                        defaultValue:
                                                                            "Delivery Provider",
                                                                    }
                                                                )}
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        freeSolo
                                                        loading={customerLoading}
                                                        options={customerOptions}
                                                        getOptionLabel={(opt) => {
                                                            if (typeof opt === "string") return opt;
                                                            return opt?.customer_name || opt?.name || "";
                                                        }}
                                                        inputValue={customerInputValue}
                                                        onInputChange={(_, value, reason) => {
                                                            if (isSyncingRef.current) return;
                                                            if (reason !== "input") return;
                                                            const nextValue = value || "";
                                                            setCustomerInputValue(nextValue);
                                                            handleInputChange("customer", nextValue);
                                                        }}
                                                        onChange={async (_, newValue) => {
                                                            if (isSyncingRef.current) return;
                                                            if (!newValue) return;
                                                            if (typeof newValue === "string") {
                                                                handleInputChange("customer", newValue);
                                                                const resolvedByText =
                                                                    await resolveCustomerByValue(newValue);
                                                                if (resolvedByText) {
                                                                    const resolvedCustomer = String(
                                                                        resolvedByText?.name || ""
                                                                    ).trim();
                                                                    if (
                                                                        hydratedCustomerRef.current !==
                                                                        resolvedCustomer
                                                                    ) {
                                                                        resetDeliveryDirtyFields();
                                                                        await applySelectedCustomer(
                                                                            resolvedByText
                                                                        );
                                                                    }
                                                                }
                                                                return;
                                                            }
                                                            const selectedCustomer = String(
                                                                newValue?.name || ""
                                                            ).trim();
                                                            if (
                                                                hydratedCustomerRef.current !==
                                                                selectedCustomer
                                                            ) {
                                                                resetDeliveryDirtyFields();
                                                                await applySelectedCustomer(newValue);
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "pos.reservationPrompt.customer",
                                                                    { defaultValue: "Customer" }
                                                                )}
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Autocomplete
                                                        freeSolo
                                                        loading={mobileLoading}
                                                        options={mobileOptions}
                                                        filterOptions={(options) => options}
                                                        getOptionLabel={(opt) => {
                                                            if (typeof opt === "string") return opt;
                                                            return opt?.mobile_no || "";
                                                        }}
                                                        inputValue={mobileInputValue}
                                                        onInputChange={(_, value, reason) => {
                                                            if (isSyncingRef.current) return;
                                                            if (reason !== "input") return;
                                                            const nextValue = value || "";
                                                            markDeliveryFieldDirty("mobile_no");
                                                            setMobileInputValue(nextValue);
                                                            handleInputChange("mobile_no", nextValue);
                                                        }}
                                                        onChange={async (_, newValue) => {
                                                            if (isSyncingRef.current) return;
                                                            if (!newValue) {
                                                                markDeliveryFieldDirty("mobile_no");
                                                                setMobileInputValue("");
                                                                handleInputChange("mobile_no", "");
                                                                return;
                                                            }
                                                            if (typeof newValue === "string") {
                                                                const typedMobile = newValue || "";
                                                                markDeliveryFieldDirty("mobile_no");
                                                                setMobileInputValue(typedMobile);
                                                                handleInputChange(
                                                                    "mobile_no",
                                                                    typedMobile
                                                                );
                                                                const resolved =
                                                                    await resolveCustomerByMobile(
                                                                        typedMobile
                                                                    );
                                                                if (resolved) {
                                                                    const resolvedMobile = String(
                                                                        resolved?.mobile_no ||
                                                                            typedMobile
                                                                    ).trim();
                                                                    if (
                                                                        hydratedMobileRef.current !==
                                                                        resolvedMobile
                                                                    ) {
                                                                        resetDeliveryDirtyFields();
                                                                        await applySelectedCustomer(
                                                                            resolved
                                                                        );
                                                                    }
                                                                }
                                                                return;
                                                            }
                                                            const selectedMobile = String(
                                                                newValue?.mobile_no || ""
                                                            ).trim();
                                                            if (
                                                                hydratedMobileRef.current !==
                                                                selectedMobile
                                                            ) {
                                                                resetDeliveryDirtyFields();
                                                                await applySelectedCustomer(newValue);
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "pos.reservationPrompt.mobileNumber",
                                                                    {
                                                                        defaultValue:
                                                                            "Mobile number",
                                                                    }
                                                                )}
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        label={t(
                                                            "pos.reservationPrompt.addressLine1",
                                                            { defaultValue: "Address line 1" }
                                                        )}
                                                        fullWidth
                                                        variant="outlined"
                                                        value={formData.address_line1}
                                                        onChange={(e) => {
                                                            markDeliveryFieldDirty(
                                                                "address_line1"
                                                            );
                                                            handleInputChange(
                                                                "address_line1",
                                                                e.target.value
                                                            );
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        label={t(
                                                            "pos.reservationPrompt.addressLine2",
                                                            { defaultValue: "Address line 2" }
                                                        )}
                                                        fullWidth
                                                        variant="outlined"
                                                        value={formData.address_line2}
                                                        onChange={(e) => {
                                                            markDeliveryFieldDirty(
                                                                "address_line2"
                                                            );
                                                            handleInputChange(
                                                                "address_line2",
                                                                e.target.value
                                                            );
                                                        }}
                                                    />
                                                </Grid>
                                            </>
                                        ) : (
                                            <>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        label="Customer"
                                                        fullWidth
                                                        variant="outlined"
                                                        value={formData.customer}
                                                        onChange={(e) =>
                                                            handleInputChange(
                                                                "customer",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </>
                            )}

                            {/* Notes Field */}
                            <TextField
                                autoFocus={!isTabsPos}
                                label="Notes"
                                fullWidth
                                variant="outlined"
                                value={formData.notes}
                                onChange={(e) =>
                                    handleInputChange("notes", e.target.value)
                                }
                                onKeyDown={handleKeyDown}
                                placeholder="Enter any special notes or requirements..."
                                multiline
                                rows={3}
                                helperText="Press Ctrl+Enter to submit quickly"
                                sx={(theme) => ({
                                    "& .MuiOutlinedInput-root": {
                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                            {
                                                borderColor:
                                                    theme.palette.primary.main,
                                            },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                            {
                                                borderColor:
                                                    theme.palette.primary.main,
                                                borderWidth: 2,
                                            },
                                    },
                                    "& .MuiFormHelperText-root": {
                                        color: theme.palette.text.disabled,
                                        fontSize: "0.8rem",
                                    },
                                })}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
                        <Button
                            onClick={handleCancel}
                            variant="outlined"
                            sx={(theme) => ({
                                borderColor: theme.palette.grey[300],
                                color: theme.palette.grey[600],
                                "&:hover": {
                                    borderColor: theme.palette.grey[400],
                                    backgroundColor: theme.palette.grey[50],
                                },
                            })}
                        >
                            {cancelText}
                        </Button>
                        {showSaveDraftButton && isTabsPos ? (
                            <Button
                                onClick={handleSaveDraft}
                                variant="outlined"
                                color="primary"
                                sx={{ minWidth: 100 }}
                            >
                                {saveDraftText}
                            </Button>
                        ) : null}
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            color="primary"
                            sx={(theme) => ({
                                minWidth: 100,
                                boxShadow:
                                    theme.customShadows?.primary ||
                                    theme.shadows[8],
                                "&:hover": {
                                    boxShadow:
                                        theme.customShadows?.primary ||
                                        theme.shadows[12],
                                },
                            })}
                        >
                            {confirmText}
                        </Button>
                    </DialogActions>
                    </Dialog>
                </ThemeProvider>
            </LocalizationProvider>
        </StyledEngineProvider>
    );
};

export const showReservationPrompt = (options = {}) => {
    return new Promise((resolve, reject) => {
        // Create a container for the dialog
        const container = document.createElement("div");
        document.body.appendChild(container);

        const root = ReactDOM.createRoot(container);

        const cleanup = () => {
            // Cleanup after dialog closes
            setTimeout(() => {
                root.unmount();
                document.body.removeChild(container);
            }, 300); // Small delay to allow dialog close animation
        };

        const handleResolve = (formData) => {
            cleanup();
            resolve(formData);
        };

        const handleReject = (error) => {
            cleanup();
            reject(error);
        };

        root.render(
            <ReservationPromptDialog
                onResolve={handleResolve}
                onReject={handleReject}
                options={options}
            />
        );
    });
};

export default ReservationPromptDialog;
